import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useResource } from "@/hooks/useResource";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { DetailDrawer } from "@/components/common/DetailDrawer";
import { LoadingState, EmptyState, ErrorState } from "@/components/common/States";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { apiErrorMessage } from "@/lib/api";
import { fmtCurrency } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, ShieldAlert, AlertTriangle, CheckCircle2 } from "lucide-react";

const COLS = [
  { key: "orderNumber", label: "Order #", type: "mono" },
  { key: "buyerName", label: "Buyer", type: "text" },
  { key: "status", label: "Status", type: "badge" },
  { key: "buyerPoNumber", label: "Buyer PO", type: "text" },
  { key: "deliveryDate", label: "Delivery", type: "date" },
  { key: "grandTotal", label: "Total", type: "currency" },
];

const emptyLine = () => ({ productId: "", sku: "", productName: "", productCode: "", quantity: "", unit: "pcs", rate: "", stdRate: null, priceSource: "", gstPct: "", discount: "" });

export default function Orders() {
  const qc = useQueryClient();
  const { data, isLoading, isError, error, refetch } = useResource("/orders");
  const { data: buyerData } = useResource("/buyers");
  const { data: prodData } = useResource("/products");
  const buyers = Array.isArray(buyerData) ? buyerData : [];
  const products = (Array.isArray(prodData) ? prodData : []).filter((p) => p.active);

  const [selected, setSelected] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [busy, setBusy] = useState(false);

  const [buyerId, setBuyerId] = useState("");
  const [buyerPoNumber, setBuyerPoNumber] = useState("");
  const [deliveryDate, setDeliveryDate] = useState("");
  const [priority, setPriority] = useState("Normal");
  const [remarks, setRemarks] = useState("");
  const [lines, setLines] = useState([emptyLine()]);

  const rows = Array.isArray(data) ? data : [];
  const invalidate = () => qc.invalidateQueries({ queryKey: ["resource", "/orders"] });

  const resetForm = () => {
    setBuyerId(""); setBuyerPoNumber(""); setDeliveryDate(""); setPriority("Normal"); setRemarks(""); setLines([emptyLine()]);
  };
  const openCreate = () => { resetForm(); setFormOpen(true); };

  const setLine = (i, patch) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  const pickProduct = async (i, id) => {
    const p = products.find((x) => x.id === id);
    const base = { productId: id, sku: p?.sku || "", productName: p?.name || "", productCode: p?.productId || "", unit: p?.productUnit || "pcs", stdRate: p?.sellingPrice ?? null, rate: p?.sellingPrice ?? "", priceSource: "STANDARD" };
    setLine(i, base);
    if (buyerId && id) {
      try {
        const res = await api.get("/buyer-prices/resolve", { params: { buyerId, productId: id } });
        const r = res.data || {};
        setLine(i, { rate: r.price ?? base.rate, unit: r.unit || base.unit, gstPct: r.gstPct ?? "", discount: r.discount ?? "", priceSource: r.source || "STANDARD", stdRate: p?.sellingPrice ?? null });
      } catch { /* keep standard */ }
    }
  };

  const lineAmount = (l) => {
    const gross = (Number(l.quantity) || 0) * (Number(l.rate) || 0);
    const disc = gross * ((Number(l.discount) || 0) / 100);
    return gross - disc;
  };
  const subtotal = lines.reduce((s, l) => s + lineAmount(l), 0);
  const priceMismatch = lines.some((l) => l.productId && l.stdRate != null && Number(l.rate) !== Number(l.stdRate));

  const validate = () => {
    if (!buyerId) { toast.error("Select a buyer"); return false; }
    const valid = lines.filter((l) => l.productId && l.quantity);
    if (valid.length === 0) { toast.error("Add at least one product line"); return false; }
    return true;
  };

  const handleConfirm = async () => {
    setConfirmOpen(false);
    setSubmitting(true);
    const b = buyers.find((x) => x.id === buyerId);
    const payload = {
      buyerId, buyerName: b?.name || "", buyerPoNumber, source: "MANUAL",
      deliveryDate, priority, remarks, status: "DRAFT",
      lines: lines.filter((l) => l.productId && l.quantity).map((l) => ({
        sku: l.sku, productName: l.productName, productCode: l.productCode, matched: true,
        quantity: Number(l.quantity) || 0, unit: l.unit, rate: Number(l.rate) || 0,
        discount: Number(l.discount) || 0, gstPct: Number(l.gstPct) || 0, amount: lineAmount(l),
      })),
    };
    try {
      await api.post("/orders", payload);
      toast.success("Order created (Draft)");
      setFormOpen(false);
      invalidate();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Create failed"));
    } finally { setSubmitting(false); }
  };

  const confirmOrder = async () => {
    setBusy(true);
    try {
      await api.post(`/orders/${selected.id}/status`, { status: "CONFIRMED" });
      toast.success("Order confirmed");
      setDrawerOpen(false);
      invalidate();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Action failed"));
    } finally { setBusy(false); }
  };

  const status = String(selected?.status || "").toUpperCase();
  const actions = selected && (
    <>
      {status === "DRAFT" && (
        <Button data-testid="order-action-confirm" size="sm" className="gap-1.5 bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={confirmOrder}>
          <CheckCircle2 className="h-3.5 w-3.5" /> Confirm Order
        </Button>
      )}
      {busy && <Loader2 className="h-4 w-4 animate-spin text-amber-700" />}
    </>
  );

  return (
    <div className="space-y-6">
      <PageHeader title="Orders" subtitle="Sales orders — shared with the mobile app. Creating an order does not deduct stock, dispatch or invoice.">
        <Button data-testid="btn-new-order" onClick={openCreate} className="gap-2 bg-amber-700 hover:bg-amber-800">
          <Plus className="h-4 w-4" /> New Order
        </Button>
      </PageHeader>

      {isLoading ? <LoadingState label="Loading orders…" /> :
        isError ? <ErrorState message={apiErrorMessage(error)} onRetry={refetch} /> :
        rows.length === 0 ? (
          <EmptyState title="No orders yet" message="Create a sales order for a buyer."
            action={<Button data-testid="empty-new-order" onClick={openCreate} className="gap-2 bg-amber-700 hover:bg-amber-800"><Plus className="h-4 w-4" /> New Order</Button>} />
        ) : (
          <DataTable rows={rows} columns={COLS} searchKeys={["orderNumber", "buyerName", "buyerPoNumber"]} filters={[{ key: "status", label: "Status" }]} onRowClick={(r) => { setSelected(r); setDrawerOpen(true); }} testIdPrefix="order-row" />
        )}

      <DetailDrawer open={drawerOpen} onOpenChange={setDrawerOpen} title="Order" row={selected} actions={actions} />

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-h-[92vh] overflow-y-auto bg-white sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-display">New Sales Order</DialogTitle>
            <DialogDescription className="text-xs">Saved as Draft. Uses backend pricing rules; does not deduct FG stock or invoice.</DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Buyer <span className="text-rose-500">*</span></Label>
              <Select value={buyerId} onValueChange={setBuyerId}>
                <SelectTrigger data-testid="order-buyer" className="h-9 text-sm"><SelectValue placeholder="Select buyer" /></SelectTrigger>
                <SelectContent className="max-h-60 bg-white">{buyers.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Buyer PO / Reference</Label>
              <Input data-testid="order-po-ref" value={buyerPoNumber} onChange={(e) => setBuyerPoNumber(e.target.value)} className="h-9 text-sm" />
            </div>
            <div className="space-y-1.5">
              <Label>Requested Delivery</Label>
              <Input data-testid="order-delivery" type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} className="h-9 text-sm" />
            </div>
            <div className="space-y-1.5">
              <Label>Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger data-testid="order-priority" className="h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {["Low", "Normal", "High", "Urgent"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Remarks</Label>
              <Textarea data-testid="order-remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} className="min-h-[50px] text-sm" />
            </div>
          </div>

          <div className="rounded-lg border border-stone-200">
            <div className="flex items-center justify-between border-b border-stone-200 bg-stone-50 px-4 py-2">
              <span className="text-sm font-semibold text-stone-800">Products</span>
              <Button data-testid="order-add-line" size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => setLines((l) => [...l, emptyLine()])}><Plus className="h-3.5 w-3.5" /> Add</Button>
            </div>
            <div className="divide-y divide-stone-100">
              {lines.map((l, i) => (
                <div key={i} className="grid grid-cols-12 items-center gap-2 px-3 py-2">
                  <div className="col-span-4">
                    <Select value={l.productId} onValueChange={(v) => pickProduct(i, v)}>
                      <SelectTrigger data-testid={`order-line-prod-${i}`} className="h-9 text-sm"><SelectValue placeholder="Product" /></SelectTrigger>
                      <SelectContent className="max-h-60 bg-white">{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <Input data-testid={`order-line-qty-${i}`} className="col-span-2 h-9 text-sm" type="number" placeholder="Qty" value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} />
                  <div className="col-span-2 flex items-center gap-1">
                    <Input data-testid={`order-line-rate-${i}`} className="h-9 text-sm" type="number" placeholder="Rate" value={l.rate} onChange={(e) => setLine(i, { rate: e.target.value })} />
                    {l.priceSource && <Badge variant="outline" className="hidden shrink-0 text-[9px] lg:inline-flex">{l.priceSource}</Badge>}
                  </div>
                  <Input data-testid={`order-line-disc-${i}`} className="col-span-1 h-9 text-sm" type="number" placeholder="Disc%" value={l.discount} onChange={(e) => setLine(i, { discount: e.target.value })} />
                  <div className="col-span-2 text-right text-xs font-mono text-stone-700">{fmtCurrency(lineAmount(l))}</div>
                  <button data-testid={`order-line-remove-${i}`} className="col-span-1 text-stone-400 hover:text-rose-600" onClick={() => setLines((ls) => ls.filter((_, idx) => idx !== i))}><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
            <div className="flex justify-end border-t border-stone-200 bg-stone-50 px-4 py-2 text-sm">
              <span className="text-stone-500">Subtotal:&nbsp;</span>
              <span data-testid="order-subtotal" className="font-mono font-semibold text-stone-900">{fmtCurrency(subtotal)}</span>
            </div>
          </div>

          {priceMismatch && (
            <div data-testid="order-price-warning" className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              A line rate differs from the resolved buyer/standard price. Review before confirming — the entered price will be used as-is.
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button data-testid="order-save" className="bg-amber-700 hover:bg-amber-800" disabled={submitting} onClick={() => validate() && setConfirmOpen(true)}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Order"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-amber-600" /> Confirm write to live ERP</AlertDialogTitle>
            <AlertDialogDescription>This creates a Draft sales order in the shared database (visible on mobile too). It does not deduct stock, dispatch, or invoice. Proceed?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="order-confirm" className="bg-amber-700 hover:bg-amber-800" onClick={handleConfirm}>Yes, create</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
